import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const services = [
  {
    icon: "ri-draft-line",
    title: "3D & 2D Design",
    desc: "Professional AutoCAD engineering design in 3D and 2D — full drawing packages, utility layouts, and KEBS-ready submissions.",
    img: "/images/home-services/design.webp",
    link: "/design-services",
    color: "from-teal-500 to-cyan-500",
  },
  {
    icon: "ri-restaurant-2-line",
    title: "Commercial Kitchen",
    desc: "End-to-end design, fabrication and installation of professional SS304 commercial kitchen equipment for restaurants, hotels and institutions.",
    img: "/images/home-services/kitchen.webp",
    link: "/commercial-kitchen",
    color: "from-emerald-500 to-teal-500",
  },
  {
    icon: "ri-temp-hot-line",
    title: "HVAC Systems",
    desc: "Heating, Ventilation & Air Conditioning — custom-engineered, installed and maintained for commercial and industrial clients across Kenya.",
    img: "/images/home-services/hvac.webp",
    link: "/hvac-systems",
    color: "from-sky-500 to-blue-500",
  },
  {
    icon: "ri-fire-line",
    title: "Gas & TIG Welding",
    desc: "Precision gas and TIG welding for stainless steel, mild steel, and aluminium — structural, decorative and food-grade applications.",
    img: "/images/home-services/welding.webp",
    link: "/welding-services",
    color: "from-orange-500 to-amber-500",
  },
  {
    icon: "ri-drop-line",
    title: "Plumbing",
    desc: "Commercial and industrial plumbing design, installation and maintenance — hot/cold water, drainage, gas and medical gas systems.",
    img: "/images/home-services/plumbing.webp",
    link: "/plumbing-services",
    color: "from-teal-500 to-emerald-600",
  },
  {
    icon: "ri-hospital-line",
    title: "Hospital & Pharmacy",
    desc: "SS316 pharmacy counters, sluice rooms, theatre equipment and medical storage solutions — HACCP and infection-control compliant.",
    img: "/images/home-services/hospital.webp",
    link: "/hospital-pharmacy",
    color: "from-rose-500 to-pink-500",
  },
  {
    icon: "ri-tools-line",
    title: "Food Processing",
    desc: "HACCP-ready food processing equipment — mixing tanks, conveyors, cutting tables and silos — custom-fabricated in food-grade SS304.",
    img: "/images/home-services/food.webp",
    link: "/food-processing",
    color: "from-amber-500 to-orange-500",
  },
  {
    icon: "ri-building-line",
    title: "Stainless Steel Railing",
    desc: "Architecturally designed SS304/SS316 balustrades, handrails and staircase railings — mirror, satin or powder-coated finishes.",
    img: "/images/home-services/railing.webp",
    link: "/stainless-railing",
    color: "from-slate-500 to-gray-600",
  },
];

function ServiceCard({ service, index }: { service: typeof services[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 14;
    const y = -((e.clientY - rect.top) / rect.height - 0.5) * 10;
    setTilt({ x, y });
  };

  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
        transitionDelay: `${index * 80}ms`,
      }}
    >
      <Link to={service.link} className="block h-full">
        <div
          className="relative bg-white rounded-xl overflow-hidden border border-gray-100 h-full cursor-pointer group"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => { setHovered(false); setTilt({ x: 0, y: 0 }); }}
          onMouseMove={handleMouseMove}
          style={{
            transform: hovered
              ? `perspective(800px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg) scale(1.03)`
              : "perspective(800px) rotateX(0) rotateY(0) scale(1)",
            transition: hovered ? "transform 0.1s ease" : "transform 0.4s ease",
            boxShadow: hovered ? "0 20px 50px rgba(0,0,0,0.12), 0 0 0 1px rgba(20,184,166,0.3)" : "0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          {/* Image */}
          <div className="relative h-44 overflow-hidden">
            <img
              src={service.img}
              alt={service.title}
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
            />
            <div className={`absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent`}></div>
            {/* Icon badge */}
            <div className={`absolute bottom-3 left-3 w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br ${service.color}`}>
              <i className={`${service.icon} text-white text-lg`}></i>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <h3 className="text-gray-900 font-bold text-sm uppercase tracking-wide mb-2 group-hover:text-teal-700 transition-colors">
              {service.title}
            </h3>
            <p className="text-gray-500 text-xs leading-relaxed">{service.desc}</p>
            <div className="flex items-center gap-1 mt-4 text-teal-600 text-xs font-semibold group-hover:gap-2 transition-all">
              View Details <i className="ri-arrow-right-line"></i>
            </div>
          </div>

          {/* Top-edge gradient line on hover */}
          <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${service.color} transition-opacity duration-300 ${hovered ? "opacity-100" : "opacity-0"}`}></div>
        </div>
      </Link>
    </div>
  );
}

export default function Services() {
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setHeaderVisible(true); obs.disconnect(); } }, { threshold: 0.2 });
    if (headerRef.current) obs.observe(headerRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="services" className="py-24 bg-gray-50 overflow-hidden">
      {/* Animated section header */}
      <div ref={headerRef} className="relative w-full h-64 mb-16 overflow-hidden">
        <img
          src="/images/hero/welding.webp"
          alt="Engineering Services"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <span
            className="text-amber-400 text-xs font-semibold uppercase tracking-widest mb-3 transition-all duration-700"
            style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? "translateY(0)" : "translateY(20px)", transitionDelay: "0.1s" }}
          >
            What We Do
          </span>
          <h2
            className="text-white text-4xl md:text-5xl font-extrabold uppercase tracking-tight transition-all duration-700"
            style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? "translateY(0)" : "translateY(20px)", transitionDelay: "0.25s" }}
          >
            Our <span className="text-teal-400">8 Core</span> Services
          </h2>
          <p
            className="text-white/70 text-sm mt-3 max-w-lg transition-all duration-700"
            style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? "translateY(0)" : "translateY(20px)", transitionDelay: "0.4s" }}
          >
            End-to-end engineering solutions — from concept to commissioning and ongoing maintenance.
          </p>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((service, i) => (
            <ServiceCard key={service.title} service={service} index={i} />
          ))}
        </div>

        {/* CTA strip */}
        <div className="mt-14 rounded-xl overflow-hidden relative">
          <img
            src="/images/kitchen/worktops.webp"
            alt="CTA"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/95 via-teal-800/90 to-teal-900/80"></div>
          <div className="relative px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-teal-300 text-xs uppercase tracking-widest font-semibold mb-1">Limited Time Offer</p>
              <h3 className="text-white text-2xl md:text-3xl font-extrabold">
                Save Up To <span className="text-amber-400">20% OFF</span> on All Services
              </h3>
              <p className="text-teal-200/70 text-sm mt-1">Contact us today — offer valid while slots last.</p>
            </div>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
              className="group bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold px-8 py-3.5 rounded-lg uppercase text-sm tracking-wider transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-calendar-check-line"></i>
              Book Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
